import * as Tone from 'tone';

// Improved audio waveform analyzer
export const analyzeAudioBuffer = (audioBuffer) => {
    const channelData = audioBuffer.getChannelData(0);
    const duration = audioBuffer.duration;

    // Dynamically adjust sample count based on audio length
    const targetSamples = Math.min(2000, Math.max(200, duration * 50));
    const blockSize = Math.floor(channelData.length / targetSamples);
    const waveformData = [];

    for (let i = 0; i < targetSamples; i++) {
        let min = 1.0;
        let max = -1.0;
        let rms = 0;

        const blockStart = i * blockSize;
        const blockEnd = Math.min(blockStart + blockSize, channelData.length);
        const samplesInBlock = blockEnd - blockStart;

        for (let j = blockStart; j < blockEnd; j++) {
            const sample = channelData[j] || 0;
            if (sample < min) min = sample;
            if (sample > max) max = sample;
            rms += sample * sample;
        }

        rms = Math.sqrt(rms / samplesInBlock);

        waveformData.push({
            min: Math.max(-1, min),
            max: Math.min(1, max),
            rms: rms
        });
    }

    return waveformData;
};

// Process audio file and return clip data
export const processAudioFile = async (file, trackId, startTime, bpm) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const audioBuffer = await Tone.context.decodeAudioData(e.target.result);

                // Calculate audio duration in beats
                const durationInBeats = (audioBuffer.duration * bpm) / 60;

                console.log(`📁 Processing: ${file.name}`);
                console.log(`  Duration: ${audioBuffer.duration.toFixed(2)}s = ${durationInBeats.toFixed(2)} beats`);

                const waveform = analyzeAudioBuffer(audioBuffer);

                const clipDuration = Math.max(0.25, durationInBeats);
                const audioClip = {
                    id: Date.now() + Math.random(),
                    name: file.name.replace(/\.[^/.]+$/, ""),
                    start: Math.round(startTime * 4) / 4,
                    duration: clipDuration,
                    sourceDuration: clipDuration, // original content length — used for loop playback
                    loop: false,
                    type: 'audio',
                    audioBuffer: audioBuffer,
                    audioLength: audioBuffer.duration,
                    fadeIn: 0,
                    fadeOut: 0,
                    gain: 1,
                    playbackRate: 1,
                    waveform: waveform
                };

                resolve(audioClip);

            } catch (error) {
                reject(new Error(`Audio processing failed: ${error.message}`));
            }
        };

        reader.onerror = () => reject(new Error('File processing failed'));
        reader.readAsArrayBuffer(file);
    });
};

// Format time display
export const formatTime = (time) => {
    const bars = Math.floor(time / 4) + 1;
    const beats = Math.floor(time % 4) + 1;
    const sixteenths = Math.floor((time % 1) * 4) + 1;
    return `${String(bars).padStart(3, '0')}:${beats}:${sixteenths}`;
};

// Calculate timeline length dynamically
export const calculateTimelineLength = (tracks) => {
    let maxEndTime = 32; // Default minimum 32 beats (8 bars)

    tracks.forEach(track => {
        track.clips.forEach(clip => {
            const clipEndTime = clip.start + clip.duration;
            if (clipEndTime > maxEndTime) {
                maxEndTime = clipEndTime;
            }
        });
    });

    // Add some extra space, ensure it's a multiple of 4 (bar alignment)
    const paddedLength = Math.ceil((maxEndTime + 16) / 4) * 4;
    return Math.max(paddedLength, 32);
}; 